/**
 * Settings > Users, two-step verification section (ADMIN; tracker item 63):
 * who has MFA on or is locked out, and a reset for another user who lost
 * their phone or is locked. The admin re-enters their own password (and
 * their own code when they use MFA); an admin's own MFA is changed on the
 * security page, never here.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { createAPIClient, IMfaApi } from '../api';
import { MfaReauth, UserMfaStatus } from '../api/mfaTypes';
import * as types from '../api/types';
import { getErrorMessage } from '../utils/apiError';
import { rowDangerActionClass } from './formStyles';
import { Toast } from './Toast';
import { MfaReauthForm } from './MfaReauthForm';

interface UserMfaPanelProps {
  users: Pick<types.UserListItem, 'id' | 'email' | 'role' | 'isActive'>[];
  currentUserId: string | undefined;
}

export function UserMfaPanel({ users, currentUserId }: UserMfaPanelProps) {
  const api = React.useMemo<IMfaApi>(() => createAPIClient(), []);
  const [statuses, setStatuses] = useState<Map<string, UserMfaStatus>>(new Map());
  const [selfUsesMfa, setSelfUsesMfa] = useState(false);
  const [resetting, setResetting] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [rows, own] = await Promise.all([api.listUserMfaStatuses(), api.getMfaStatus()]);
      setStatuses(new Map(rows.map((r) => [r.userId, r])));
      setSelfUsesMfa(own.enabled);
      setLoadError(null);
    } catch (err) {
      setLoadError(getErrorMessage(err, 'Could not load two-step verification status'));
    }
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  const staff = users.filter((u) => u.role !== types.UserRole.CUSTOMER_PORTAL);

  const reset = async (userId: string, reauth: MfaReauth) => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.resetUserMfa(userId, reauth);
      const email = staff.find((u) => u.id === userId)?.email ?? 'the user';
      setNotice(result.hadMfa ? `Two-step verification reset for ${email}. They can set it up again.` : `${email} had no two-step verification; any lock was cleared.`);
      setResetting(null);
      await load();
    } catch (err) {
      setError(getErrorMessage(err, 'Could not reset two-step verification'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-label="Two-step verification" className="mt-8">
      <h2 className="text-lg font-semibold text-gray-900">Two-step verification</h2>
      <p className="mt-1 text-sm text-gray-600">
        Reset a user who lost their phone and recovery codes, or who is locked after too many wrong codes. They sign in with their password and set it up again.
      </p>
      {loadError && <Toast kind="error">{loadError}</Toast>}
      {notice && <Toast kind="success">{notice}</Toast>}
      <ul className="mt-3 divide-y divide-gray-200 rounded-md border border-gray-200 bg-white">
        {staff.map((u) => {
          const s = statuses.get(u.id);
          const self = u.id === currentUserId;
          const canReset = !self && !!s && (s.enabled || !!s.lockedUntil);
          return (
            <li key={u.id} className="p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-gray-900">{u.email}</p>
                  <p className="text-xs text-gray-600" data-testid={`mfa-state-${u.id}`}>
                    {s?.enabled ? 'On' : 'Off'}
                    {s?.lockedUntil ? ' · locked' : ''}
                    {self ? ' · you (change it on your security page)' : ''}
                  </p>
                </div>
                {canReset && resetting !== u.id && (
                  <button
                    type="button"
                    className={rowDangerActionClass}
                    onClick={() => {
                      setResetting(u.id);
                      setError(null);
                      setNotice(null);
                    }}
                  >
                    Reset two-step verification
                  </button>
                )}
              </div>
              {resetting === u.id && (
                <div className="mt-3">
                  <MfaReauthForm
                    idPrefix={`mfa-reset-${u.id}`}
                    needsFactor={selfUsesMfa}
                    submitLabel="Reset"
                    busy={busy}
                    error={error}
                    danger
                    onSubmit={(reauth) => reset(u.id, reauth)}
                    onCancel={() => setResetting(null)}
                    intro={`Confirm it is you. ${u.email} will have to set up two-step verification again.`}
                  />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
